import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  HeaderProps,
  NavItem as NavItemType,
  NavItemConfig,
  NavItemGroup,
} from './Header.types'
import {
  StyledHeader,
  LogoSection,
  NavSection,
  NavItem,
  NavItemWrapper,
  SubmenuContainer,
  SubmenuItem,
  SubmenuArrow,
  SearchSection,
  MobileMenuButton,
  DesktopOnly,
  UserMenu,
  UserAvatar,
  UserName,
  Avatar,
  MobileMenu,
  MobileNavSection,
  MobileNavItem,
  MobileSubmenu,
  MobileSubmenuItem,
} from './Header.styles'
import { Text } from '../../atoms/Text'
import { Icon } from '../../atoms/Icon'
import { Input } from '../../atoms/Input'

// Swipe configuration
const SWIPE_THRESHOLD = 50 // pixels to swipe before closing
const SWIPE_VELOCITY_THRESHOLD = 0.3 // pixels per ms

function isNavItemGroup(item: NavItemConfig): item is NavItemGroup {
  return 'items' in item
}

function isModifiedEvent(event: React.MouseEvent): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
}

function getNavItemKey(item: NavItemConfig, index: number): string {
  if ('id' in item && item.id) return item.id
  if (typeof item.label === 'string') return item.label

  return `nav-item-${index}`
}

export const Header: React.FC<HeaderProps> = ({
  logo,
  title,
  showSearch = false,
  searchPlaceholder = 'Search...',
  onSearchChange,
  navItems = [],
  actions,
  user,
  onUserClick,
  variant = 'default',
  sticky = false,
  className,
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null)
  const [_mobileOpenSubmenu, _setMobileOpenSubmenu] = useState<string | null>(
    null
  )
  const [mobileExpandedSubmenus, setMobileExpandedSubmenus] = useState<
    Set<string>
  >(new Set())
  const [swipeOffset, setSwipeOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const submenuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const touchStartX = useRef(0)
  const touchStartTime = useRef(0)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchValue(value)
    onSearchChange?.(value)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openSubmenu) {
        const submenuRef = submenuRefs.current[openSubmenu]
        if (submenuRef && !submenuRef.contains(event.target as Node)) {
          setOpenSubmenu(null)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openSubmenu])

  useEffect(() => {
    // Close mobile menu when resizing to desktop
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    // Initialize expanded submenus when mobile menu opens
    if (mobileMenuOpen && mobileExpandedSubmenus.size === 0) {
      const expandedSet = new Set<string>()
      navItems.forEach((item, index) => {
        if (isNavItemGroup(item)) {
          expandedSet.add(getNavItemKey(item, index))
        }
      })
      setMobileExpandedSubmenus(expandedSet)
    }
  }, [mobileMenuOpen, navItems, mobileExpandedSubmenus.size])

  const handleNavClick = (
    event: React.MouseEvent,
    item: NavItemConfig,
    itemKey: string
  ) => {
    if (isNavItemGroup(item)) {
      event.preventDefault()
      setOpenSubmenu(openSubmenu === itemKey ? null : itemKey)
    } else if (isModifiedEvent(event)) {
      // Let browser handle Cmd/Ctrl+click natively (open in new tab)
      return
    } else if (item.onClick) {
      event.preventDefault()
      item.onClick()
      setOpenSubmenu(null)
    }
  }

  const handleSubmenuClick = (
    event: React.MouseEvent,
    subItem: NavItemType
  ) => {
    if (isModifiedEvent(event)) {
      // Let browser handle Cmd/Ctrl+click natively
      return
    }
    event.preventDefault()
    subItem.onClick?.()
    setOpenSubmenu(null)
  }

  const handleMobileNavClick = (
    event: React.MouseEvent,
    item: NavItemConfig,
    itemKey: string
  ) => {
    if (isNavItemGroup(item)) {
      event.preventDefault()
      const newExpanded = new Set(mobileExpandedSubmenus)
      if (newExpanded.has(itemKey)) {
        newExpanded.delete(itemKey)
      } else {
        newExpanded.add(itemKey)
      }
      setMobileExpandedSubmenus(newExpanded)
    } else if (isModifiedEvent(event)) {
      return
    } else if (item.onClick) {
      event.preventDefault()
      item.onClick()
      setMobileMenuOpen(false)
    }
  }

  const handleMobileSubmenuClick = (
    event: React.MouseEvent,
    subItem: NavItemType
  ) => {
    if (isModifiedEvent(event)) {
      return
    }
    event.preventDefault()
    subItem.onClick?.()
    setMobileMenuOpen(false)
  }

  // Reset swipe offset when menu closes
  useEffect(() => {
    if (!mobileMenuOpen) {
      setSwipeOffset(0)
      setIsDragging(false)
    }
  }, [mobileMenuOpen])

  // Swipe handlers for mobile menu (swipe left to close)
  const handleMobileMenuTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    touchStartX.current = touch.clientX
    touchStartTime.current = Date.now()
    setIsDragging(true)
  }, [])

  const handleMobileMenuTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return

      const touch = e.touches[0]
      const deltaX = touch.clientX - touchStartX.current

      // Only allow swiping left (negative delta)
      if (deltaX < 0) {
        setSwipeOffset(Math.abs(deltaX))
      }
    },
    [isDragging]
  )

  const handleMobileMenuTouchEnd = useCallback(() => {
    if (!isDragging) return

    const elapsed = Date.now() - touchStartTime.current
    const velocity = swipeOffset / elapsed

    // Close if swiped past threshold or with high velocity
    if (swipeOffset > SWIPE_THRESHOLD || velocity > SWIPE_VELOCITY_THRESHOLD) {
      setMobileMenuOpen(false)
    }

    setSwipeOffset(0)
    setIsDragging(false)
  }, [isDragging, swipeOffset])

  // Calculate mobile menu transform based on swipe
  const mobileMenuStyle =
    swipeOffset > 0
      ? {
          transform: `translateX(-${swipeOffset}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
        }
      : undefined

  return (
    <StyledHeader $sticky={sticky} $variant={variant} className={className}>
      {variant !== 'minimal' && (
        <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <Icon name={mobileMenuOpen ? 'close' : 'menu'} />
        </MobileMenuButton>
      )}

      {(logo || title) && (
        <LogoSection>
          {logo}
          {title && (
            <Text variant="h5" weight="semibold">
              {title}
            </Text>
          )}
        </LogoSection>
      )}

      <DesktopOnly>
        {navItems.length > 0 && variant !== 'minimal' && (
          <NavSection $variant={variant}>
            {navItems.map((item, index) => {
              const isGroup = isNavItemGroup(item)
              const itemKey = getNavItemKey(item, index)
              const isOpen = isGroup && openSubmenu === itemKey

              return (
                <NavItemWrapper
                  key={itemKey}
                  ref={(el) => {
                    if (isGroup && el) {
                      submenuRefs.current[itemKey] = el
                    }
                  }}
                >
                  <NavItem
                    $active={!!item.active}
                    href={!isGroup && 'href' in item ? item.href : undefined}
                    onClick={(e: React.MouseEvent) =>
                      handleNavClick(e, item, itemKey)
                    }
                  >
                    {item.label}
                    {isGroup && (
                      <SubmenuArrow
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      >
                        <path d="M6 9l6 6 6-6" />
                      </SubmenuArrow>
                    )}
                  </NavItem>
                  {isGroup && (
                    <SubmenuContainer $isOpen={isOpen}>
                      {item.items.map((subItem, subIndex) => (
                        <SubmenuItem
                          key={subIndex}
                          $active={subItem.active}
                          href={subItem.href}
                          onClick={(e: React.MouseEvent) =>
                            handleSubmenuClick(e, subItem)
                          }
                        >
                          {subItem.icon}
                          {subItem.label}
                        </SubmenuItem>
                      ))}
                    </SubmenuContainer>
                  )}
                </NavItemWrapper>
              )
            })}
          </NavSection>
        )}

        {showSearch && (
          <SearchSection>
            <Input
              fullWidth
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={handleSearchChange}
              leftIcon={<Icon name="search" size="small" />}
            />
          </SearchSection>
        )}
      </DesktopOnly>

      <UserMenu>
        <DesktopOnly>{actions && actions}</DesktopOnly>

        {user && (
          <UserAvatar onClick={onUserClick}>
            <UserName>{user.name}</UserName>
            <Avatar>
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                />
              ) : (
                user.initials
              )}
            </Avatar>
          </UserAvatar>
        )}
      </UserMenu>

      {/* Mobile Menu - swipe left to close */}
      <MobileMenu
        ref={mobileMenuRef}
        $isOpen={mobileMenuOpen}
        onTouchStart={handleMobileMenuTouchStart}
        onTouchMove={handleMobileMenuTouchMove}
        onTouchEnd={handleMobileMenuTouchEnd}
        style={mobileMenuStyle}
      >
        {variant !== 'minimal' && (
          <MobileNavSection>
            {navItems.map((item, index) => {
              const isGroup = isNavItemGroup(item)
              const itemKey = getNavItemKey(item, index)
              const isOpen = isGroup && mobileExpandedSubmenus.has(itemKey)

              return (
                <div key={itemKey}>
                  <MobileNavItem
                    $active={!!item.active}
                    href={!isGroup && 'href' in item ? item.href : undefined}
                    onClick={(e: React.MouseEvent) =>
                      handleMobileNavClick(e, item, itemKey)
                    }
                  >
                    {item.label}
                    {isGroup && (
                      <SubmenuArrow
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          width: '16px',
                          height: '16px',
                        }}
                      >
                        <path d="M6 9l6 6 6-6" />
                      </SubmenuArrow>
                    )}
                  </MobileNavItem>
                  {isGroup && (
                    <MobileSubmenu $isOpen={isOpen}>
                      {item.items.map((subItem, subIndex) => (
                        <MobileSubmenuItem
                          key={subIndex}
                          $active={subItem.active}
                          href={subItem.href}
                          onClick={(e: React.MouseEvent) =>
                            handleMobileSubmenuClick(e, subItem)
                          }
                        >
                          {subItem.icon}
                          {subItem.label}
                        </MobileSubmenuItem>
                      ))}
                    </MobileSubmenu>
                  )}
                </div>
              )
            })}
          </MobileNavSection>
        )}

        {showSearch && (
          <div style={{ marginTop: '16px' }}>
            <Input
              fullWidth
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={handleSearchChange}
              leftIcon={<Icon name="search" size="small" />}
            />
          </div>
        )}

        {actions && <div style={{ marginTop: '16px' }}>{actions}</div>}
      </MobileMenu>
    </StyledHeader>
  )
}
